import { useQuery } from '@tanstack/react-query';
import {
  guidedPlanningQuoteKeys,
  guidedPlanningApi,
} from '@/features/guided-financial-planning/api';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

export function useGuidedPlanningQuotes(
  clientId: string,
  session: GuidedPlanningSessionResponse | undefined,
) {
  const { flags } = useFeatureFlags();
  const sessionId = session?.id;
  const selectedCategories = session?.stepData?.selectedPolicyCategories;
  const paymentPreference = session?.stepData?.preferences?.paymentPreference;

  const query = useQuery({
    // Include selectedCategories in query key so changes trigger refetch
    queryKey: guidedPlanningQuoteKeys.list({
      clientId,
      sessionId,
      selectedCategories,
      paymentPreference,
    }),
    queryFn: () => guidedPlanningApi.getQuotes(clientId, sessionId!),
    // Only fetch when we have a session with selected categories
    enabled: Boolean(
      clientId &&
        sessionId &&
        selectedCategories?.length &&
        session?.status !== 'completed',
    ),
    staleTime: 5 * 60 * 1000, // 5 minutes - quotes don't change often
  });

  return {
    ...query,
    // Extract quotes array for backward compatibility
    data: flags.nycLifeDemo
      ? query.data?.quotes?.filter(quote => quote?.fundingPolicyType !== 2936)
      : query.data?.quotes,
    // Expose metadata for empty state messaging
    noQuotesReason: query.data?.noQuotesReason,
    // True when preferred policy types had no matches and all quotes are shown instead
    isFallback: query.data?.isFallback ?? false,
    // Expose refetching state for filter changes
    isRefetching: query.isRefetching,
  };
}
